import { useQuery } from '@apollo/client/react';
import { Box, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';

import { type ListingInsightsQueryVariables } from '@graphql/__generated__/graphql';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { getLotStatusLabel } from '@modules/_shared/components/LotCardInfo';
import { LISTING_INSIGHTS } from '@modules/listings/listingActivityQueries';
import { useLocale } from '@shared/hooks/locale';

import { InfoTip } from './ChartControls';
import { useDaysOnMarket } from './useDaysOnMarket';

// A momentum reading: this week's count vs the average week over the prior 4
// weeks. `delta` is null when there's no prior baseline to compare against.
type Momentum = { recent: number; delta: number | null };

const Trend = ({ delta }: { delta: number | null }) => {
  const { t, locale } = useLocale();
  if (delta == null) {
    return (
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {t('No prior data')}
      </Typography>
    );
  }
  const flat = Math.abs(delta) < 0.05;
  const up = delta > 0;
  const color = flat ? 'text.secondary' : up ? 'success.main' : 'error.main';
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color }}>
      <Icon size={14} />
      <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
        {`${up && !flat ? '+' : ''}${(delta * 100).toLocaleString(locale, {
          maximumFractionDigits: 0,
        })}%`}
      </Typography>
    </Box>
  );
};

const PulseTile = ({
  label,
  help,
  value,
  footer,
}: {
  label: string;
  help: string;
  value: React.ReactNode;
  footer: React.ReactNode;
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      p: 1.25,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <InfoTip title={help} />
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
      {value}
    </Typography>
    <Box sx={{ mt: 0.25 }}>{footer}</Box>
  </Box>
);

export const ListingPulseCard = ({
  lotId,
  insightsVars,
}: {
  lotId: string;
  insightsVars: ListingInsightsQueryVariables;
}) => {
  const { t, locale, dateLocale } = useLocale();
  const dom = useDaysOnMarket(lotId);
  const { data, error: insightsError } = useQuery(LISTING_INSIGHTS, {
    variables: insightsVars,
  });

  // Prior window is 4 weeks (priorFrom → recentFrom), so the comparable weekly
  // baseline is the prior total / 4.
  const toMomentum = (recent: number, prior: number): Momentum => {
    const priorPerWeek = prior / 4;
    return {
      recent,
      delta: priorPerWeek > 0 ? (recent - priorPerWeek) / priorPerWeek : null,
    };
  };

  const enquiries = toMomentum(
    data?.recent_enquiries.aggregate?.count ?? 0,
    data?.prior_enquiries.aggregate?.count ?? 0,
  );
  const viewings = toMomentum(
    data?.recent_viewings.aggregate?.count ?? 0,
    data?.prior_viewings.aggregate?.count ?? 0,
  );

  const partialCoverage =
    (dom.state === 'live' || dom.state === 'paused') &&
    dom.coverage === 'partial';
  const partialDom = partialCoverage && dom.since == null ? dom : null;

  const domValue = (() => {
    switch (dom.state) {
      case 'live':
      case 'paused':
        return t('{{count}} day', { count: dom.days });
      case 'closed':
        return getLotStatusLabel(dom.lotStatus, t);
      case 'unpublished':
        return t('Not published');
      case 'unknown':
        return t('History unavailable');
      case 'error':
        return t('Failed to load data');
      default:
        return '—';
    }
  })();

  const domFooter = (() => {
    switch (dom.state) {
      case 'live':
      case 'paused':
        return (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            <Box component="span" sx={{ display: 'block' }}>
              {partialDom != null
                ? t(
                    'Tracking publication history since {{date}}. Earlier history is unavailable.',
                    {
                      date: format(partialDom.observedSince, 'd MMM yyyy', {
                        locale: dateLocale,
                      }),
                    },
                  )
                : dom.since != null
                ? t('First published on {{date}}', {
                    date: format(dom.since, 'd MMM yyyy', {
                      locale: dateLocale,
                    }),
                  })
                : null}
            </Box>
            {dom.state === 'paused' ? (
              <Box component="span" sx={{ display: 'block' }}>
                {t('Currently offline on all portals')}
              </Box>
            ) : null}
          </Typography>
        );
      case 'closed':
        return dom.since != null ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('First published on {{date}}', {
              date: format(dom.since, 'd MMM yyyy', { locale: dateLocale }),
            })}
          </Typography>
        ) : null;
      default:
        return null;
    }
  })();

  return (
    <ListingAccordion title={t('Pulse')} defaultExpanded>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
        <PulseTile
          label={
            partialDom != null ? t('Tracked days online') : t('Days on market')
          }
          help={
            partialCoverage
              ? t(
                  'Total time online observed since tracking began. Time offline on all portals is excluded.',
                )
              : t(
                  'Total time online on at least one portal. Time offline on all portals is excluded.',
                )
          }
          value={domValue}
          footer={domFooter}
        />
        <PulseTile
          label={t('Enquiries this week')}
          help={t(
            'Enquiries in the last 7 days compared to the average week over the prior 4 weeks.',
          )}
          value={
            insightsError == null
              ? enquiries.recent.toLocaleString(locale)
              : '—'
          }
          footer={
            insightsError == null ? (
              <Trend delta={enquiries.delta} />
            ) : (
              <Typography variant="caption" color="error">
                {t('Failed to load data')}
              </Typography>
            )
          }
        />
        <PulseTile
          label={t('Viewings this week')}
          help={t(
            'Viewings that took place in the last 7 days compared to the average week over the prior 4 weeks.',
          )}
          value={
            insightsError == null ? viewings.recent.toLocaleString(locale) : '—'
          }
          footer={
            insightsError == null ? (
              <Trend delta={viewings.delta} />
            ) : (
              <Typography variant="caption" color="error">
                {t('Failed to load data')}
              </Typography>
            )
          }
        />
      </Stack>
    </ListingAccordion>
  );
};
